/**
 * MindMapNode Component
 * Custom node with editable label and styled shapes
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Handle,
  Position,
  NodeResizer,
  ResizeParams,
  useUpdateNodeInternals,
} from "@xyflow/react";
import { Box, TextField, Typography, alpha } from "@mui/material";
import {
  CategoryRounded as CategoryRoundedIcon,
  FlagRounded as FlagRoundedIcon,
  HubRounded as HubRoundedIcon,
  LightbulbRounded as LightbulbRoundedIcon,
  NotesRounded as NotesRoundedIcon,
  TaskAltRounded as TaskAltRoundedIcon,
} from "@mui/icons-material";
import { useColorMode } from "../../../contexts/AppThemeProvider";
import { MindMapNodeData, MindMapNodeType } from "./mindMapTypes";

const nodeStyles: Record<
  MindMapNodeType,
  { borderRadius: string; minWidth: number; defaultWidth: number }
> = {
  topic: { borderRadius: "24px", minWidth: 80, defaultWidth: 120 },
  idea: { borderRadius: "16px", minWidth: 60, defaultWidth: 100 },
  note: { borderRadius: "4px", minWidth: 50, defaultWidth: 80 },
  goal: { borderRadius: "18px", minWidth: 70, defaultWidth: 110 },
  step: { borderRadius: "12px", minWidth: 55, defaultWidth: 88 },
  category: { borderRadius: "14px", minWidth: 70, defaultWidth: 120 },
  break: { borderRadius: "10px", minWidth: 34, defaultWidth: 46 },
  junction: { borderRadius: "0px", minWidth: 1, defaultWidth: 1 },
};

const getNodeTypeIcon = (nodeType: MindMapNodeType) => {
  switch (nodeType) {
    case "topic":
      return <HubRoundedIcon sx={{ fontSize: 16 }} />;
    case "idea":
      return <LightbulbRoundedIcon sx={{ fontSize: 16 }} />;
    case "note":
      return <NotesRoundedIcon sx={{ fontSize: 16 }} />;
    case "goal":
      return <FlagRoundedIcon sx={{ fontSize: 16 }} />;
    case "step":
      return <TaskAltRoundedIcon sx={{ fontSize: 16 }} />;
    case "category":
      return <CategoryRoundedIcon sx={{ fontSize: 16 }} />;
    case "break":
    case "junction":
    default:
      return null;
  }
};

interface MindMapNodeProps {
  id: string;
  data: MindMapNodeData;
  type?: string;
  selected?: boolean;
}

const MindMapNode = ({
  id,
  data,
  type = "idea",
  selected = false,
}: MindMapNodeProps) => {
  const { mode } = useColorMode();
  const updateNodeInternals = useUpdateNodeInternals();
  const minutesToHoursString = useCallback((minutes?: number) => {
    if (typeof minutes !== "number" || !Number.isFinite(minutes)) return "";
    const hrs = minutes / 60;
    if (hrs <= 0) return "";
    const rounded = Math.round(hrs * 100) / 100;
    return String(rounded);
  }, []);

  const parseHoursToMinutes = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const hours = Number(trimmed);
    if (!Number.isFinite(hours) || hours < 0) return null;
    return Math.round(hours * 60);
  }, []);

  const parseDollars = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const dollars = Number(trimmed);
    if (!Number.isFinite(dollars) || dollars < 0) return null;
    return Math.round(dollars * 100) / 100;
  }, []);

  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);
  const [description, setDescription] = useState(data.description || "");
  const [goalDateDate, setGoalDateDate] = useState(
    (data.goalDate || "").split("T")[0] || ""
  );
  const [goalDateTime, setGoalDateTime] = useState(() => {
    const raw = data.goalDate || "";
    const parts = raw.split("T");
    if (parts.length < 2) return "";
    return (parts[1] || "").slice(0, 5);
  });
  const [goalAmount, setGoalAmount] = useState(
    typeof data.goalAmount === "number" && Number.isFinite(data.goalAmount)
      ? String(data.goalAmount)
      : ""
  );
  const [stepHours, setStepHours] = useState(
    minutesToHoursString(data.stepTimeEstimateMinutes)
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const nodeType = type as MindMapNodeType;
  const style = nodeStyles[nodeType] || nodeStyles.idea;
  const goalLocked =
    nodeType === "goal" &&
    typeof data.lockedFromGoalId === "string" &&
    data.lockedFromGoalId.length > 0;
  const typeIcon = useMemo(() => getNodeTypeIcon(nodeType), [nodeType]);

  useEffect(() => {
    setLabel(data.label);
    setDescription(data.description || "");
    setGoalDateDate((data.goalDate || "").split("T")[0] || "");
    setGoalDateTime(() => {
      const raw = data.goalDate || "";
      const parts = raw.split("T");
      if (parts.length < 2) return "";
      return (parts[1] || "").slice(0, 5);
    });
    setGoalAmount(
      typeof data.goalAmount === "number" && Number.isFinite(data.goalAmount)
        ? String(data.goalAmount)
        : ""
    );
    setStepHours(minutesToHoursString(data.stepTimeEstimateMinutes));
  }, [
    data.label,
    data.description,
    data.goalDate,
    data.goalAmount,
    data.stepTimeEstimateMinutes,
    minutesToHoursString,
  ]);

  const associationSummary = useMemo(() => {
    const list = (data.associations || []) as NonNullable<
      MindMapNodeData["associations"]
    >;
    if (!list || list.length === 0) return null;
    const filtered = list
      .filter((a) => a.kind !== "housingForecast")
      .filter((a) => (nodeType === "goal" ? a.kind !== "goal" : true));
    if (!filtered || filtered.length === 0) return null;
    const countsFiltered = filtered.reduce(
      (acc, a) => {
        acc[a.kind] += 1;
        return acc;
      },
      {
        goal: 0,
        debt: 0,
        expense: 0,
        income: 0,
        housingForecast: 0,
      } as Record<
        "goal" | "debt" | "expense" | "income" | "housingForecast",
        number
      >
    );
    const parts = (
      Object.keys(countsFiltered) as Array<keyof typeof countsFiltered>
    )
      .filter((k) => countsFiltered[k] > 0)
      .map((k) => `${k}:${countsFiltered[k]}`);
    return parts.join("  ");
  }, [data.associations, nodeType]);

  const moneySummary = useMemo(() => {
    const list = (data.associations || []) as NonNullable<
      MindMapNodeData["associations"]
    >;
    if (!list || list.length === 0) return null;

    const totals = list.reduce(
      (acc, a) => {
        if (typeof a.signedAmount !== "number") return acc;
        if (a.kind === "income") acc.income += a.signedAmount;
        if (a.kind === "expense") acc.expense += a.signedAmount;
        if (a.kind === "debt") acc.debt += a.signedAmount;
        return acc;
      },
      { income: 0, expense: 0, debt: 0 }
    );

    if (
      Math.abs(totals.income) < 0.005 &&
      Math.abs(totals.expense) < 0.005 &&
      Math.abs(totals.debt) < 0.005
    ) {
      return null;
    }

    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });

    return { fmt, totals };
  }, [data.associations]);

  const housingNetSummary = useMemo(() => {
    const list = (data.associations || []) as NonNullable<
      MindMapNodeData["associations"]
    >;
    const housing = list.filter((a) => a.kind === "housingForecast");
    if (housing.length === 0) return null;

    const net = housing.reduce((sum, a) => {
      const useNoTax = Boolean(a.excludeCapitalGainsTax);
      const v = useNoTax ? a.housingNetCashNoTax : a.housingNetCash;
      return sum + (typeof v === "number" ? v : 0);
    }, 0);

    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });

    return { fmt, net };
  }, [data.associations, nodeType]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = useCallback(() => {
    if (nodeType === "break" || nodeType === "junction") return;
    setIsEditing(true);
  }, [nodeType]);

  const commitChanges = useCallback(() => {
    setIsEditing(false);
    const nextDescription = goalLocked
      ? data.description || ""
      : description.trim();
    const normalizedGoalDate = goalDateDate.trim()
      ? `${goalDateDate.trim()}T${(goalDateTime.trim() || "00:00").slice(0, 5)}`
      : "";
    const nextGoalAmount =
      nodeType === "goal" && !goalLocked ? parseDollars(goalAmount) : undefined;
    const existingGoalAmount =
      nodeType === "goal" && !goalLocked ? data.goalAmount : undefined;
    const nextStepMinutes =
      nodeType === "step" ? parseHoursToMinutes(stepHours) : undefined;
    const existingStepMinutes =
      nodeType === "step" ? data.stepTimeEstimateMinutes : undefined;
    if (
      (!goalLocked && label !== data.label) ||
      (!goalLocked && nextDescription !== (data.description || "")) ||
      normalizedGoalDate !== (data.goalDate || "") ||
      (nodeType === "goal" &&
        !goalLocked &&
        nextGoalAmount !== existingGoalAmount) ||
      (nodeType === "step" && nextStepMinutes !== existingStepMinutes)
    ) {
      // Dispatch custom event for label change
      const event = new CustomEvent("mindmap:labelchange", {
        detail: {
          id,
          label: goalLocked ? data.label : label,
          description: goalLocked ? data.description || "" : nextDescription,
          goalDate: normalizedGoalDate || undefined,
          goalAmount:
            nodeType === "goal" && !goalLocked
              ? nextGoalAmount === null
                ? null
                : nextGoalAmount
              : undefined,
          stepTimeEstimateMinutes:
            nodeType === "step"
              ? nextStepMinutes === null
                ? null
                : nextStepMinutes
              : undefined,
        },
      });
      window.dispatchEvent(event);
    }
  }, [
    id,
    label,
    description,
    goalDateDate,
    goalDateTime,
    goalAmount,
    stepHours,
    nodeType,
    goalLocked,
    data.label,
    data.description,
    data.goalDate,
    data.goalAmount,
    data.stepTimeEstimateMinutes,
    parseHoursToMinutes,
    parseDollars,
  ]);

  const handleEditorBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const nextTarget = e.relatedTarget as Node | null;
      if (nextTarget && editorRef.current?.contains(nextTarget)) {
        return;
      }
      commitChanges();
    },
    [commitChanges]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitChanges();
      } else if (e.key === "Escape") {
        setLabel(data.label);
        setDescription(data.description || "");
        setGoalDateDate((data.goalDate || "").split("T")[0] || "");
        setGoalDateTime(() => {
          const raw = data.goalDate || "";
          const parts = raw.split("T");
          if (parts.length < 2) return "";
          return (parts[1] || "").slice(0, 5);
        });
        setGoalAmount(
          typeof data.goalAmount === "number" &&
            Number.isFinite(data.goalAmount)
            ? String(data.goalAmount)
            : ""
        );
        setStepHours(minutesToHoursString(data.stepTimeEstimateMinutes));
        setIsEditing(false);
      }
    },
    [
      commitChanges,
      data.label,
      data.description,
      data.goalDate,
      data.goalAmount,
      data.stepTimeEstimateMinutes,
      minutesToHoursString,
    ]
  );

  const handleResize = useCallback(
    (_event: unknown, params: ResizeParams) => {
      // Dispatch custom event for size change
      const event = new CustomEvent("mindmap:sizechange", {
        detail: { id, width: params.width, height: params.height },
      });
      window.dispatchEvent(event);
    },
    [id]
  );

  const handleResizeEnd = useCallback(() => {
    // Resizing changes the DOM positions of handles; tell React Flow to
    // recompute handle bounds so edges anchor to the correct points.
    requestAnimationFrame(() => updateNodeInternals(id));
  }, [id, updateNodeInternals]);

  const isDark = mode === "dark";

  const getNodeColor = () => {
    switch (nodeType) {
      case "topic":
        return isDark ? "#5dc4b2" : "#2bb39d";
      case "idea":
        return isDark ? "#6b8afd" : "#4f6ef7";
      case "note":
        return isDark ? "#f59e0b" : "#d97706";
      case "goal":
        return isDark ? "#f97316" : "#ea580c";
      case "step":
        return isDark ? "#facc15" : "#eab308";
      case "category":
        return isDark ? "#a855f7" : "#7c3aed";
      case "break":
        return isDark ? "#f87171" : "#ef4444";
      case "junction":
        return "transparent";
      default:
        return isDark ? "#5dc4b2" : "#2bb39d";
    }
  };

  const getNodeBackground = () => {
    const base = getNodeColor();
    // Opaque base + tint overlay so edges never show "under" nodes.
    const tint = isDark ? alpha(base, 0.28) : alpha(base, 0.18);
    const baseBg = isDark ? "#1a1a1a" : "#ffffff";
    return `linear-gradient(180deg, ${tint}, ${tint}), ${baseBg}`;
  };

  const getNodeBorderColor = () => {
    const base = getNodeColor();
    return selected ? base : isDark ? alpha(base, 0.45) : alpha(base, 0.35);
  };

  const borderStyle =
    nodeType === "topic" ||
    nodeType === "goal" ||
    nodeType === "step" ||
    nodeType === "category"
      ? "solid"
      : "dotted";
  const borderWidth =
    nodeType === "topic" ||
    nodeType === "goal" ||
    nodeType === "step" ||
    nodeType === "category"
      ? 2
      : 3;

  if (nodeType === "junction") {
    const handleStyle = {
      background: "transparent",
      width: 10,
      height: 10,
      border: "0px solid transparent",
      opacity: 0,
      pointerEvents: "none" as const,
    };

    return (
      <Box sx={{ width: 1, height: 1, opacity: 0, pointerEvents: "none" }}>
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={handleStyle}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={handleStyle}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={handleStyle}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={handleStyle}
        />
      </Box>
    );
  }

  return (
    <>
      <NodeResizer
        minWidth={style.minWidth}
        minHeight={nodeType === "break" ? 20 : 40}
        isVisible={selected && nodeType !== "break"}
        onResize={handleResize}
        onResizeEnd={handleResizeEnd}
        lineStyle={{
          border: "none",
        }}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: getNodeColor(),
          border: `1px solid ${isDark ? "#1a1a1a" : "#fff"}`,
        }}
      />
      <Box
        onDoubleClick={handleDoubleClick}
        sx={{
          px: nodeType === "break" ? 1 : 2,
          py:
            nodeType === "topic" ||
            nodeType === "goal" ||
            nodeType === "category"
              ? 2
              : nodeType === "break"
                ? 0.6
                : nodeType === "step"
                  ? 1.25
                  : 1.5,
          width: "100%",
          height: "auto",
          minWidth: style.minWidth,
          minHeight: 40,
          borderRadius: style.borderRadius,
          background: getNodeBackground(),
          border: `${borderWidth}px ${borderStyle} ${getNodeBorderColor()}`,
          boxShadow: selected
            ? `0 0 0 2px ${getNodeColor()}40`
            : "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          boxSizing: "border-box",
          overflow: "visible",
          "&:hover": {
            borderColor: getNodeColor(),
          },
        }}
      >
        {/* One handle per side */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{
            background: getNodeColor(),
            width: 12,
            height: 12,
            border: `2px solid ${isDark ? "#1a1a1a" : "#fff"}`,
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{
            background: getNodeColor(),
            width: 12,
            height: 12,
            border: `2px solid ${isDark ? "#1a1a1a" : "#fff"}`,
          }}
        />

        {nodeType === "break" ? (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.78rem",
                letterSpacing: 0.5,
                color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.8)",
                userSelect: "none",
              }}
            >
              -/ /-
            </Typography>
          </Box>
        ) : isEditing ? (
          <Box
            ref={editorRef}
            onBlur={handleEditorBlur}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {goalLocked && (
              <Typography
                variant="caption"
                sx={{
                  textAlign: "center",
                  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                }}
              >
                Linked Goal: fields are locked
              </Typography>
            )}
            <TextField
              inputRef={inputRef}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              slotProps={{
                htmlInput: { maxLength: 120, readOnly: goalLocked },
              }}
              sx={{
                width: "100%",
                "& .MuiInput-input": {
                  textAlign: "center",
                  fontSize:
                    nodeType === "topic" ||
                    nodeType === "goal" ||
                    nodeType === "category"
                      ? "1rem"
                      : nodeType === "step"
                        ? "0.82rem"
                        : "0.875rem",
                  fontWeight:
                    nodeType === "topic" ||
                    nodeType === "goal" ||
                    nodeType === "category"
                      ? 600
                      : nodeType === "step"
                        ? 600
                        : 400,
                  p: 0,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
              }}
            />
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              placeholder="Description"
              slotProps={{
                htmlInput: { maxLength: 240, readOnly: goalLocked },
              }}
              sx={{
                width: "100%",
                "& .MuiInput-input": {
                  textAlign: "center",
                  fontSize: "0.72rem",
                  color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                  p: 0,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
              }}
            />
            <TextField
              value={goalDateDate}
              onChange={(e) => setGoalDateDate(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              type="date"
              placeholder="Goal Date"
              sx={{
                width: "100%",
                "& .MuiInput-input": {
                  textAlign: "center",
                  fontSize: "0.72rem",
                  color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                  p: 0,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
              }}
            />
            <TextField
              value={goalDateTime}
              onChange={(e) => setGoalDateTime(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              type="time"
              placeholder="Time (optional)"
              sx={{
                width: "100%",
                "& .MuiInput-input": {
                  textAlign: "center",
                  fontSize: "0.72rem",
                  color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                  p: 0,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
              }}
            />
            {nodeType === "goal" && (
              <TextField
                value={goalAmount}
                onChange={(e) => setGoalAmount(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                variant="standard"
                type="number"
                placeholder="Amount ($)"
                slotProps={{
                  htmlInput: {
                    min: 0,
                    readOnly: goalLocked,
                    step: 0.01,
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInput-input": {
                    textAlign: "center",
                    fontSize: "0.72rem",
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    p: 0,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottom: "none",
                  },
                }}
              />
            )}
            {nodeType === "step" && (
              <TextField
                value={stepHours}
                onChange={(e) => setStepHours(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                variant="standard"
                type="number"
                placeholder="Time (hours)"
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: 0.25,
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInput-input": {
                    textAlign: "center",
                    fontSize: "0.72rem",
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    p: 0,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottom: "none",
                  },
                }}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.3,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              {typeIcon && (
                <Box
                  sx={{
                    flex: "0 0 auto",
                    color: isDark
                      ? "rgba(255,255,255,0.78)"
                      : "rgba(0,0,0,0.62)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {typeIcon}
                </Box>
              )}
              <Typography
                variant={
                  nodeType === "topic" ||
                  nodeType === "goal" ||
                  nodeType === "category"
                    ? "body1"
                    : "body2"
                }
                fontWeight={nodeType === "note" ? 400 : 600}
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  width: "100%",
                  minWidth: 0,
                  color: isDark ? "#fff" : "#1a1a1a",
                  textAlign: "center",
                }}
              >
                {label}
              </Typography>
            </Box>
            {description.trim() && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  width: "100%",
                  color: isDark ? "rgba(255,255,255,0.62)" : "rgba(0,0,0,0.54)",
                }}
              >
                {description}
              </Typography>
            )}
            {data.goalDate && (
              <Typography
                sx={{
                  fontSize: "0.64rem",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                  width: "100%",
                  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                }}
              >
                Goal:{" "}
                {new Date(data.goalDate).toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </Typography>
            )}
            {nodeType === "goal" &&
              typeof data.goalAmount === "number" &&
              Number.isFinite(data.goalAmount) &&
              data.goalAmount > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.64rem",
                    lineHeight: 1.15,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    width: "100%",
                    color: isDark
                      ? "rgba(255,255,255,0.55)"
                      : "rgba(0,0,0,0.5)",
                  }}
                >
                  Amount:{" "}
                  {new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  }).format(data.goalAmount)}
                </Typography>
              )}
            {nodeType === "step" &&
              typeof data.stepTimeEstimateMinutes === "number" &&
              data.stepTimeEstimateMinutes > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.64rem",
                    lineHeight: 1.15,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    width: "100%",
                    color: isDark
                      ? "rgba(255,255,255,0.55)"
                      : "rgba(0,0,0,0.5)",
                  }}
                >
                  Est: {minutesToHoursString(data.stepTimeEstimateMinutes)}h
                </Typography>
              )}
            {associationSummary && (
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  width: "100%",
                  color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
                }}
              >
                {associationSummary}
              </Typography>
            )}
            {moneySummary && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  lineHeight: 1.15,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  gap: 0.75,
                }}
              >
                {Math.abs(moneySummary.totals.income) >= 0.005 && (
                  <span style={{ color: "#22C55E" }}>
                    +{moneySummary.fmt.format(moneySummary.totals.income)}
                  </span>
                )}
                {Math.abs(moneySummary.totals.expense) >= 0.005 && (
                  <span style={{ color: "#EF4444" }}>
                    {moneySummary.fmt.format(moneySummary.totals.expense)}
                  </span>
                )}
                {Math.abs(moneySummary.totals.debt) >= 0.005 && (
                  <span style={{ color: "#EF4444" }}>
                    {moneySummary.fmt.format(moneySummary.totals.debt)}
                  </span>
                )}
              </Typography>
            )}
            {housingNetSummary && (
              <Typography
                sx={{
                  fontSize: "0.64rem",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                  width: "100%",
                  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                }}
              >
                Net: {housingNetSummary.fmt.format(housingNetSummary.net)}
              </Typography>
            )}
          </Box>
        )}

        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{
            background: getNodeColor(),
            width: 12,
            height: 12,
            border: `2px solid ${isDark ? "#1a1a1a" : "#fff"}`,
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{
            background: getNodeColor(),
            width: 12,
            height: 12,
            border: `2px solid ${isDark ? "#1a1a1a" : "#fff"}`,
          }}
        />
      </Box>
    </>
  );
};

export default MindMapNode;
